import { SupabaseClient } from '@supabase/supabase-js'
import { Database, Tables } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import { PermAction, PermOptions, permOptionsKey, TableWithPermissions } from '@/types/base/permissions'
import Authuser, { authCanPerform, evaluatePermissions } from '@/types/base/authuser'
import { getWorkspaceById } from './workspaces'

const logger = createIsomorphicLogger('db.assistants')

export const getAssistantById = async (supabase: SupabaseClient<Database>, assistantId: string) => {
  const { data: assistant, error } = await supabase
    .from("assistants")
    .select("*")
    .eq("id", assistantId)
    .maybeSingle()

  if (error) {
		logError(logger, error, 'Failed to fetch Assistant', { assistantId })
    throw new Error(error.message)
  }

  return assistant
}

export const getOptionalAssistantByName = async (supabase: SupabaseClient<Database>, assistantName: string,
		workspaceId: string) => {
  const { data, error } = await supabase
    .from("workspaces")
    .select(
      `
      assistants (*)
    `
    )
    .eq("id", workspaceId)
    .eq("assistants.name", assistantName)
    .maybeSingle()

  if (error) {
		logError(logger, error, 'Failed to fetch Assistant', { assistantName: printObject (assistantName) })
    throw new Error(error.message)
  }

  return data ? data.assistants[0] : null
}

export const getAssistantWorkspacesByWorkspaceId = async (
	supabase: SupabaseClient<Database>,
  workspaceId: string
) => {
  const { data: workspace, error } = await supabase
    .from("workspaces")
    .select(
      `
      id,
      name,
      assistants (*)
    `
    )
    .eq("id", workspaceId)
    .single()

  if (!workspace) {
		logError(logger, error, 'Failed to fetch Assistant', { workspaceId })
    throw new Error(error.message)
  }

  return workspace.assistants ?? []
}

export const getAssistantsWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'assistants'>[]> => {
	const wsAssistants = await getAssistantWorkspacesByWorkspaceId(supabase, workspaceId)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'assistants'>[] = []

	for (const [i, assistant] of wsAssistants.entries()) {
		const perms = await evaluatePermissions(supabase, authuser,
			{ type: "assistants", context: "ANY", uuid: assistant.id, tenant:assistant.owning_tenant, ws: workspace})

		if (i === 0) { // Attach special non specific permission to first record only
			const options = { type: "assistants", action: PermAction.create, context: "aidonAdmin" } as PermOptions
			const allowed = await authCanPerform(supabase, authuser, options)
			const shareKey = permOptionsKey(options)
			perms[shareKey] = allowed
		}

		result.push({ ...assistant, permissions: perms })
	}

	return result
}

export const getAssistantWorkspacesByAssistantId = async (
	supabase: SupabaseClient<Database>,
  assistantId: string
) => {
  const { data: assistant, error } = await supabase
    .from("assistants")
    .select(
      `
      id,
      name,
      workspaces (*)
    `
    )
    .eq("id", assistantId)
    .single()

  if (!assistant) {
		logError(logger, error, 'Failed to fetch Assistant', { assistantId })
    throw new Error(error.message)
  }

  return assistant
}

export const getAssistantRelatedByWorkspaceId = async (
	supabase: SupabaseClient<Database>,
  workspaceId: string
) => {
	const { data: assistantWorkspaces, error: assistantWorkspacesError } = await supabase
		.from('assistant_workspaces')
		.select('assistant_id')
		.eq('workspace_id', workspaceId)

	if (!assistantWorkspaces || assistantWorkspaces.length === 0) {
		return []
	}

	const assistantIds = assistantWorkspaces.map(({ assistant_id }) => assistant_id)

	const { data, error } = await supabase
		.from('assistants')
		.select(`
			*,
			files:assistant_files (*),
			collections:assistant_collections (*),
			tools:assistant_tools (*)
		`)
		.in('id', assistantIds)
		.order('id', { ascending: true })

	if (error) {
		logError(logger, error, 'Failed to fetch Assistant', { assistantIds: printObject(assistantIds) })
		throw new Error(error.message)
	}

	return data
}

export const getAutoShareAssistants = async (supabase: SupabaseClient<Database>) => {
  const { data: assistants, error } = await supabase
    .from("assistants")
    .select("*")
    .eq("auto_share", true)

  if (error) {
		logError(logger, error, 'Failed to fetch auto share Assistants')
    throw new Error(error.message)
  }

  return assistants
}

export const createAssistant = async (
	supabase: SupabaseClient<Database>,
  assistant: TablesInsert<"assistants">,
  workspace: Tables<"workspaces">
) => {
	const assistantWithTenant = {
		...assistant,
		owning_tenant: workspace.tenant_id
	}

  const { data: createdAssistant, error } = await supabase
    .from("assistants")
    .insert([assistantWithTenant])
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to create assistant', { assistant: printObject(assistant) })
    throw new Error(error.message)
  }

  await createAssistantWorkspace(supabase, {
    user_id: createdAssistant.user_id,
    assistant_id: createdAssistant.id,
    workspace_id: workspace.id
  })

	logger.info('Assistant created successfully', { assistantId: createdAssistant.id })
  return createdAssistant
}

export const createAssistants = async (
	supabase: SupabaseClient<Database>,
  assistants: TablesInsert<"assistants">[],
  workspace: Tables<"workspaces">
) => {
	const assistantsWithTenant = assistants.map(assistant => ({
		...assistant,
		owning_tenant: workspace.tenant_id
	}))

  const { data: createdAssistants, error } = await supabase
    .from("assistants")
    .insert(assistantsWithTenant)
    .select("*")

  if (error) {
		logError(logger, error, 'Failed to create assistants', { assistants: printObject(assistants) })
    throw new Error(error.message)
  }

  await createAssistantWorkspaces(supabase,
    createdAssistants.map(assistant => ({
      user_id: assistant.user_id,
      assistant_id: assistant.id,
	    workspace_id: workspace.id
    }))
  )

	logger.info('Assistants created successfully', { assistantIds: createdAssistants.map(assistant => assistant.id) })
  return createdAssistants
}

export const createAssistantWorkspace = async (supabase: SupabaseClient<Database>, item: {
  user_id: string
  assistant_id: string
  workspace_id: string
}) => {
  const { data: createdAssistantWorkspace, error } = await supabase
    .from("assistant_workspaces")
    .insert([item])
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to create assistant workspace', { item: printObject(item) })
    throw new Error(error.message)
  }

	logger.info('Assistant workspace created successfully', { assistantWorkspace: printObject(createdAssistantWorkspace) })
  return createdAssistantWorkspace
}

export const createAssistantWorkspaces = async (
	supabase: SupabaseClient<Database>,
  items: { user_id: string; assistant_id: string; workspace_id: string }[]
) => {
  const { data: createdAssistantWorkspaces, error } = await supabase
    .from("assistant_workspaces")
    .upsert(items, { onConflict: 'assistant_id, workspace_id' })
    .select("*")

  if (error) {
		logError(logger, error, 'Failed to create assistant workspaces', { items: printObject(items) })
		throw new Error(error.message)
	}

	logger.info('Assistant workspaces created successfully', { assistantWorkspaces: printObject(createdAssistantWorkspaces) })
  return createdAssistantWorkspaces
}

export const updateAssistant = async (
	supabase: SupabaseClient<Database>,
  assistantId: string,
  assistant: TablesUpdate<"assistants">
) => {
  const { data: updatedAssistant, error } = await supabase
    .from("assistants")
    .update(assistant)
    .eq("id", assistantId)
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to update assistant', { assistant: printObject(assistant) })
    throw new Error(error.message)
  }

  return updatedAssistant
}

export const deleteAssistant = async (supabase: SupabaseClient<Database>, assistantId: string) => {
  const { error } = await supabase
    .from("assistants")
    .delete()
    .eq("id", assistantId)

  if (error) {
		logError(logger, error, 'Failed to delete assistant', { assistantId })
    throw new Error(error.message)
  }

  return true
}

export const deleteAssistantWorkspace = async (
	supabase: SupabaseClient<Database>,
  assistantId: string,
  workspaceId: string
) => {
  const { error } = await supabase
    .from("assistant_workspaces")
    .delete()
    .eq("assistant_id", assistantId)
    .eq("workspace_id", workspaceId)

  if (error) {
		logError(logger, error, 'Failed to delete assistant workspace', { assistantId, workspaceId })
		throw new Error(error.message)
	}

  return true
}
